/**
 * NAICS (North American Industry Classification System) Data
 *
 * Standard US government business classification system.
 * 20 sectors (2-digit), subsectors (3-digit), industry groups (4-digit).
 *
 * The LLM composition engine uses NAICS codes to determine which
 * workflows, rules, and compliance frameworks apply to each tenant.
 *
 * Source: US Census Bureau NAICS 2022
 */

// ============================================================================
// Sector Data (2-digit codes)
// ============================================================================

export interface NaicsSector {
  code: string;
  label: string;
  description: string;
}

export const NAICS_SECTORS: NaicsSector[] = [
  { code: '11', label: 'Agriculture, Forestry, Fishing and Hunting', description: 'Crop production, animal production, forestry, logging, fishing, hunting' },
  { code: '21', label: 'Mining, Quarrying, and Oil and Gas Extraction', description: 'Oil/gas extraction, mining, support activities for mining' },
  { code: '22', label: 'Utilities', description: 'Electric power, natural gas, water, sewage, steam supply' },
  { code: '23', label: 'Construction', description: 'Building construction, heavy/civil engineering, specialty trades' },
  { code: '31-33', label: 'Manufacturing', description: 'Food, textiles, wood, chemicals, metals, machinery, electronics' },
  { code: '42', label: 'Wholesale Trade', description: 'Durable goods, nondurable goods, electronic markets, agents/brokers' },
  { code: '44-45', label: 'Retail Trade', description: 'Motor vehicles, furniture, electronics, food/beverage, apparel, general merchandise' },
  { code: '48-49', label: 'Transportation and Warehousing', description: 'Air, rail, water, truck, transit, pipeline, scenic/sightseeing, warehousing' },
  { code: '51', label: 'Information', description: 'Publishing, motion picture, broadcasting, telecommunications, data processing' },
  { code: '52', label: 'Finance and Insurance', description: 'Banking, securities, insurance carriers, funds/trusts' },
  { code: '53', label: 'Real Estate and Rental and Leasing', description: 'Real estate, rental/leasing, lessors' },
  { code: '54', label: 'Professional, Scientific, and Technical Services', description: 'Legal, accounting, architecture, engineering, consulting, research, advertising' },
  { code: '55', label: 'Management of Companies and Enterprises', description: 'Holding companies, corporate offices, regional management' },
  { code: '56', label: 'Administrative and Support Services', description: 'Office admin, facilities support, employment services, security, waste management' },
  { code: '61', label: 'Educational Services', description: 'Schools, colleges, training, technical/trade schools' },
  { code: '62', label: 'Health Care and Social Assistance', description: 'Ambulatory care, hospitals, nursing facilities, social assistance' },
  { code: '71', label: 'Arts, Entertainment, and Recreation', description: 'Performing arts, spectator sports, museums, amusement, gambling, recreation' },
  { code: '72', label: 'Accommodation and Food Services', description: 'Hotels, RV parks, restaurants, bars, catering, food trucks' },
  { code: '81', label: 'Other Services (except Public Administration)', description: 'Repair/maintenance, personal care, laundry, religious, civic organizations' },
  { code: '92', label: 'Public Administration', description: 'Executive, legislative, justice, public order, human resource programs' },
];

// Mantine Select-compatible data
export const NAICS_SECTOR_OPTIONS = NAICS_SECTORS.map((s) => ({
  value: s.code,
  label: `${s.label} (${s.code})`,
}));

// ============================================================================
// Subsector Data (3-digit codes) — key subsectors per sector
// ============================================================================

export interface NaicsSubsector {
  code: string;
  label: string;
}

export const NAICS_SUBSECTORS: Record<string, NaicsSubsector[]> = {
  '11': [
    { code: '111', label: 'Crop Production' },
    { code: '112', label: 'Animal Production and Aquaculture' },
    { code: '113', label: 'Forestry and Logging' },
    { code: '114', label: 'Fishing, Hunting and Trapping' },
    { code: '115', label: 'Support Activities for Agriculture and Forestry' },
  ],
  '21': [
    { code: '211', label: 'Oil and Gas Extraction' },
    { code: '212', label: 'Mining (except Oil and Gas)' },
    { code: '213', label: 'Support Activities for Mining' },
  ],
  '22': [
    { code: '221', label: 'Utilities' },
  ],
  '23': [
    { code: '236', label: 'Construction of Buildings' },
    { code: '237', label: 'Heavy and Civil Engineering Construction' },
    { code: '238', label: 'Specialty Trade Contractors' },
  ],
  '31-33': [
    { code: '311', label: 'Food Manufacturing' },
    { code: '312', label: 'Beverage and Tobacco Product Manufacturing' },
    { code: '313', label: 'Textile Mills' },
    { code: '314', label: 'Textile Product Mills' },
    { code: '315', label: 'Apparel Manufacturing' },
    { code: '321', label: 'Wood Product Manufacturing' },
    { code: '322', label: 'Paper Manufacturing' },
    { code: '323', label: 'Printing and Related Support Activities' },
    { code: '324', label: 'Petroleum and Coal Products Manufacturing' },
    { code: '325', label: 'Chemical Manufacturing' },
    { code: '326', label: 'Plastics and Rubber Products Manufacturing' },
    { code: '327', label: 'Nonmetallic Mineral Product Manufacturing' },
    { code: '331', label: 'Primary Metal Manufacturing' },
    { code: '332', label: 'Fabricated Metal Product Manufacturing' },
    { code: '333', label: 'Machinery Manufacturing' },
    { code: '334', label: 'Computer and Electronic Product Manufacturing' },
    { code: '335', label: 'Electrical Equipment and Appliance Manufacturing' },
    { code: '336', label: 'Transportation Equipment Manufacturing' },
    { code: '337', label: 'Furniture and Related Product Manufacturing' },
    { code: '339', label: 'Miscellaneous Manufacturing' },
  ],
  '42': [
    { code: '423', label: 'Merchant Wholesalers, Durable Goods' },
    { code: '424', label: 'Merchant Wholesalers, Nondurable Goods' },
    { code: '425', label: 'Wholesale Trade Agents and Brokers' },
  ],
  '44-45': [
    { code: '441', label: 'Motor Vehicle and Parts Dealers' },
    { code: '442', label: 'Furniture and Home Furnishings Stores' },
    { code: '443', label: 'Electronics and Appliance Stores' },
    { code: '444', label: 'Building Material and Garden Supply Dealers' },
    { code: '445', label: 'Food and Beverage Retailers' },
    { code: '446', label: 'Health and Personal Care Retailers' },
    { code: '447', label: 'Gasoline Stations' },
    { code: '448', label: 'Clothing and Clothing Accessories Stores' },
    { code: '449', label: 'Furniture, Home Furnishings, Electronics, and Appliance Retailers' },
    { code: '451', label: 'Sporting Goods, Hobby, Musical Instrument, and Book Stores' },
    { code: '452', label: 'General Merchandise Stores' },
    { code: '453', label: 'Miscellaneous Store Retailers' },
    { code: '454', label: 'Nonstore Retailers' },
  ],
  '48-49': [
    { code: '481', label: 'Air Transportation' },
    { code: '482', label: 'Rail Transportation' },
    { code: '483', label: 'Water Transportation' },
    { code: '484', label: 'Truck Transportation' },
    { code: '485', label: 'Transit and Ground Passenger Transportation' },
    { code: '486', label: 'Pipeline Transportation' },
    { code: '487', label: 'Scenic and Sightseeing Transportation' },
    { code: '488', label: 'Support Activities for Transportation' },
    { code: '491', label: 'Postal Service' },
    { code: '492', label: 'Couriers and Messengers' },
    { code: '493', label: 'Warehousing and Storage' },
  ],
  '51': [
    { code: '511', label: 'Publishing Industries (except Internet)' },
    { code: '512', label: 'Motion Picture and Sound Recording Industries' },
    { code: '513', label: 'Broadcasting and Content Providers' },
    { code: '516', label: 'Computing Infrastructure Providers, Data Processing, and Related Services' },
    { code: '517', label: 'Telecommunications' },
    { code: '518', label: 'Computing Infrastructure Providers' },
    { code: '519', label: 'Web Search Portals, Libraries, Archives, and Other Information Services' },
  ],
  '52': [
    { code: '521', label: 'Monetary Authorities - Central Bank' },
    { code: '522', label: 'Credit Intermediation and Related Activities' },
    { code: '523', label: 'Securities, Commodity Contracts, and Other Financial Investments' },
    { code: '524', label: 'Insurance Carriers and Related Activities' },
    { code: '525', label: 'Funds, Trusts, and Other Financial Vehicles' },
  ],
  '53': [
    { code: '531', label: 'Real Estate' },
    { code: '532', label: 'Rental and Leasing Services' },
    { code: '533', label: 'Lessors of Nonfinancial Intangible Assets' },
  ],
  '54': [
    { code: '5411', label: 'Legal Services' },
    { code: '5412', label: 'Accounting, Tax Preparation, Bookkeeping, and Payroll Services' },
    { code: '5413', label: 'Architectural, Engineering, and Related Services' },
    { code: '5414', label: 'Specialized Design Services' },
    { code: '5415', label: 'Computer Systems Design and Related Services' },
    { code: '5416', label: 'Management, Scientific, and Technical Consulting Services' },
    { code: '5417', label: 'Scientific Research and Development Services' },
    { code: '5418', label: 'Advertising, Public Relations, and Related Services' },
    { code: '5419', label: 'Other Professional, Scientific, and Technical Services' },
  ],
  '55': [
    { code: '551', label: 'Management of Companies and Enterprises' },
  ],
  '56': [
    { code: '561', label: 'Administrative and Support Services' },
    { code: '562', label: 'Waste Management and Remediation Services' },
  ],
  '61': [
    { code: '611', label: 'Educational Services' },
  ],
  '62': [
    { code: '621', label: 'Ambulatory Health Care Services' },
    { code: '622', label: 'Hospitals' },
    { code: '623', label: 'Nursing and Residential Care Facilities' },
    { code: '624', label: 'Social Assistance' },
  ],
  '71': [
    { code: '711', label: 'Performing Arts, Spectator Sports, and Related Industries' },
    { code: '712', label: 'Museums, Historical Sites, and Similar Institutions' },
    { code: '713', label: 'Amusement, Gambling, and Recreation Industries' },
  ],
  '72': [
    { code: '721', label: 'Accommodation' },
    { code: '722', label: 'Food Services and Drinking Places' },
  ],
  '81': [
    { code: '811', label: 'Repair and Maintenance' },
    { code: '812', label: 'Personal and Laundry Services' },
    { code: '813', label: 'Religious, Grantmaking, Civic, Professional, and Similar Organizations' },
    { code: '814', label: 'Private Households' },
  ],
  '92': [
    { code: '921', label: 'Executive, Legislative, and Other General Government Support' },
    { code: '922', label: 'Justice, Public Order, and Safety Activities' },
    { code: '923', label: 'Administration of Human Resource Programs' },
    { code: '924', label: 'Administration of Environmental Quality Programs' },
    { code: '925', label: 'Administration of Housing, Urban Planning, and Community Development' },
    { code: '926', label: 'Administration of Economic Programs' },
    { code: '927', label: 'Space Research and Technology' },
    { code: '928', label: 'National Security and International Affairs' },
  ],
};

// ============================================================================
// NAICS → FERP Mappings
// ============================================================================

/**
 * Sectors with payer programs (healthcare reimbursement).
 * Currently only Health Care and Social Assistance (62).
 */
export const NAICS_PAYER_SECTORS = new Set(['62']);

/**
 * Compliance framework hints by NAICS sector.
 * Shown during industry selection to inform the user.
 * The LLM composition engine determines actual requirements.
 */
export const NAICS_COMPLIANCE_HINTS: Record<string, string[]> = {
  '62': ['HIPAA', 'HITECH', 'CMS Conditions of Participation'],
  '52': ['SOX', 'Dodd-Frank', 'SEC/FINRA', 'BSA/AML'],
  '54': ['SOX (if public)', 'Professional liability standards'],
  '22': ['NERC CIP', 'EPA regulations'],
  '31-33': ['OSHA', 'EPA', 'FDA (food/pharma)'],
  '23': ['OSHA', 'Davis-Bacon Act', 'Workers comp'],
  '72': ['FDA Food Code', 'OSHA', 'Health department permits'],
  '61': ['FERPA', 'Title IX', 'ADA compliance'],
  '11': ['USDA regulations', 'EPA', 'OSHA'],
  '48-49': ['DOT/FMCSA', 'OSHA', 'Hazmat regulations'],
  '21': ['MSHA', 'EPA', 'OSHA'],
  '81': ['OSHA', 'State licensing boards'],
  '51': ['FCC regulations', 'COPPA', 'GDPR (if international)'],
  '71': ['ADA compliance', 'Liquor licensing', 'Safety codes'],
};

/**
 * Get subsector options for Mantine Select by sector code.
 */
export function getSubsectorOptions(sectorCode: string): Array<{ value: string; label: string }> {
  const subsectors = NAICS_SUBSECTORS[sectorCode] ?? [];
  return subsectors.map((s) => ({
    value: s.code,
    label: `${s.label} (${s.code})`,
  }));
}

/**
 * Get the sector code from a subsector code.
 * E.g., '621' → '62', '311' → '31-33'
 */
export function getSectorFromSubsector(subsectorCode: string): string | undefined {
  for (const [sectorCode, subsectors] of Object.entries(NAICS_SUBSECTORS)) {
    if (subsectors.some((s) => s.code === subsectorCode)) {
      return sectorCode;
    }
  }
  return undefined;
}

/**
 * Get a human-readable label for a sector code.
 */
export function getSectorLabel(sectorCode: string): string {
  return NAICS_SECTORS.find((s) => s.code === sectorCode)?.label ?? sectorCode;
}

/**
 * Get a human-readable label for a subsector code.
 */
export function getSubsectorLabel(subsectorCode: string): string {
  for (const subsectors of Object.values(NAICS_SUBSECTORS)) {
    const match = subsectors.find((s) => s.code === subsectorCode);
    if (match) return match.label;
  }
  return subsectorCode;
}

/**
 * Check if a sector has payer programs (healthcare reimbursement).
 */
export function sectorHasPayers(sectorCode: string): boolean {
  return NAICS_PAYER_SECTORS.has(sectorCode);
}

/**
 * Get compliance hints for a sector.
 */
export function getComplianceHints(sectorCode: string): string[] {
  return NAICS_COMPLIANCE_HINTS[sectorCode] ?? [];
}
